import { useEffect, useRef, useState } from 'react';
import { useStore } from './useStore';
import api from '../api/api';
import KioAppHelper from '../helpers/kioApp';
import passKeyHelper from '../helpers/passKey';
import { KioskState } from '../typegen/kiosk-state';
import logger from '../helpers/logger';
import sqlite from '../helpers/sqlite';

let isRunning = false;

export default function useSystemAvailability() {
	const {
		configStore: { isConfigLoaded, companyId },
		kioskStore: { legacyIsApiActive, kioskId, lastKioskStateOnServer, updateKioskStateOnServer },
		sessionStore: { isActive: isSessionActive },
		uiStore: { showLoader, hideLoader },
	} = useStore();
	const [state, setState] = useState<KioskState>();

	const pollerRef = useRef<ReturnType<typeof setInterval> | null>(null);
	const POLL_INTERVAL_MS = 30000; // 30 seconds

	useEffect(() => {
		if (state === undefined && lastKioskStateOnServer !== undefined) setState(lastKioskStateOnServer);
	}, [lastKioskStateOnServer, state]);

	useEffect(() => {
		const handleUpdateServerState = async (state: KioskState) => {
			try {
				if (lastKioskStateOnServer !== state) await updateKioskStateOnServer(state);
			} catch (error) {
				logger.logGeneral.error({}, 'Error updating kiosk state on server:', error);
			}
		};

		const getKioskState = async () => {
			if (state === undefined || isRunning) return;
			isRunning = true;

			setState(lastKioskStateOnServer);

			if (KioAppHelper('DoUrlSubstitution', import.meta.env.VITE_ST_BPK_FORCE_UNAVAILABLE) === 'true') {
				setState(KioskState.Unavailable);
				await handleUpdateServerState(KioskState.Unavailable);
				isRunning = false;
				return;
			}

			const stateIsUnknown = lastKioskStateOnServer === KioskState.Unknown;

			if (stateIsUnknown) showLoader();

			try {
				const cashBoxAlarmTriggered = (await sqlite.getSetting('cashBoxAlarmTriggered')) === 'true';
				if (cashBoxAlarmTriggered) {
					setState(KioskState.CashBoxAlarm);
					await handleUpdateServerState(KioskState.CashBoxAlarm);
					isRunning = false;
					return;
				}

				if (lastKioskStateOnServer === KioskState.OutOfService) {
					isRunning = false;
					return;
				}

				const [legacyApi, rbpkApi] = await Promise.all([legacyIsApiActive(), api.kiosks.isAvailable()]);
				if (!legacyApi) logger.logGeneral.warn({}, 'Legacy BPK Admin API is not active');
				if (!rbpkApi) logger.logGeneral.warn({}, 'RBPK Kiosk API is not active');
				const isBothApisEnabled = !!legacyApi && !!rbpkApi;

				// If either API is unavailable, mark system as unavailable
				if (!isBothApisEnabled) {
					setState(KioskState.Unavailable);
					await handleUpdateServerState(KioskState.Unavailable);
					isRunning = false;
					return;
				}

				const passKey = await passKeyHelper.generatePassKey();
				const isMaintenanceModeOn = await api.legacyBpkAdmin.getIsMaintenanceModeOn({
					kioskID: kioskId,
					customerID: companyId,
					passKey,
				});

				// Set state based on maintenance mode
				setState(isMaintenanceModeOn ? KioskState.Maintenance : KioskState.Active);
			} catch (error) {
				setState(KioskState.Unavailable);
				logger.logGeneral.error({}, 'Error checking system availability:', error);
			} finally {
				await handleUpdateServerState(state);
				if (stateIsUnknown) hideLoader();
				isRunning = false;
			}
		};

		if (isConfigLoaded) {
			getKioskState();
			if (!isSessionActive) pollerRef.current = setInterval(getKioskState, POLL_INTERVAL_MS);
		}

		return () => {
			if (pollerRef.current) {
				clearInterval(pollerRef.current);
				pollerRef.current = null;
			}
		};
	}, [
		isConfigLoaded,
		legacyIsApiActive,
		isSessionActive,
		kioskId,
		companyId,
		lastKioskStateOnServer,
		showLoader,
		hideLoader,
		updateKioskStateOnServer,
		state,
	]);

	// Clear interval immediately when session becomes active
	useEffect(() => {
		if (isSessionActive && pollerRef.current) {
			clearInterval(pollerRef.current);
			pollerRef.current = null;
		}
	}, [isSessionActive]);

	return state;
}
